import React, { useState } from 'react';
import { FileText, Mic, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { useRecordingSessionStore } from '../../stores/useRecordingSessionStore';
import { useTranslation } from '../../hooks/useTranslation';
import { PatientDisclosureModal } from './PatientDisclosureCard';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

/**
 * Modal that appears when the doctor presses "start recording" while
 * AI-training retention is currently on for them. Confirms the doctor
 * has obtained patient consent for both the consultation recording
 * and its retention for AI improvement.
 *
 * The modal does NOT decide retention — the backend has already frozen
 * the consent state into the recording session via `decideRetention`.
 * This is the human-visible checkpoint that lives alongside the
 * machine record (consent_snapshot.doctorAttestedAt).
 *
 * Mounted once in MainLayout next to the RecordingDock so every entry
 * point that calls `startSession` is gated automatically — no callers
 * need to know about this.
 */
export const RecordingAttestationModal: React.FC = () => {
  const { isRTL } = useTranslation();
  const awaiting = useRecordingSessionStore((s) => s.awaitingAttestation);
  const confirm = useRecordingSessionStore((s) => s.confirmAttestation);
  const cancel = useRecordingSessionStore((s) => s.cancelAttestation);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  if (!awaiting) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await confirm(remember);
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel/dismiss leaves the doctor with no recording started and no
  // explanation — they tap Start, modal flashes, modal vanishes if they
  // miss the buttons. Surface a quiet breadcrumb so they know to retry.
  const handleCancel = () => {
    cancel();
    toast(
      isRTL
        ? 'لم نبدأ التسجيل — تأكيد الموافقة مطلوب قبل البدء.'
        : 'Recording not started — consent confirmation is required first.',
      { duration: 4000 },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => {
        // Click the backdrop to dismiss — same as Cancel.
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border shadow-xl"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
        }}
      >
        <header
          className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-4 border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--color-primary-700)' }}
            />
            <h3
              className="text-base font-semibold tracking-tight"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {isRTL ? 'تأكيد قبل التسجيل' : 'Confirm before recording'}
            </h3>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 rounded"
            aria-label={isRTL ? 'إغلاق' : 'Close'}
          >
            <X
              className="w-4 h-4"
              style={{ color: 'var(--color-text-tertiary)' }}
            />
          </button>
        </header>

        <div className="px-5 sm:px-6 py-5 space-y-4">
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {isRTL
              ? 'هذه الجلسة سيتم تسجيلها والاحتفاظ بها (مشفرة) لتحسين الذكاء الاصطناعي. أنت مسؤول عن إبلاغ المريض والحصول على موافقته قبل بدء التسجيل.'
              : "This session will be recorded and retained (encrypted) to improve our AI. You are responsible for informing the patient and obtaining consent before starting."}
          </p>

          <div
            className="rounded-xl p-3 text-sm leading-relaxed"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
              color: 'var(--color-text-primary)',
            }}
          >
            {isRTL
              ? '"أؤكد أنني أبلغت المريض وحصلت على موافقته على تسجيل هذه الزيارة واستخدامها لتحسين الذكاء الاصطناعي."'
              : '"I confirm that I have informed the patient and obtained their consent to record this encounter and use it for AI improvement."'}
          </div>

          <Button
            variant="link"
            size="xs"
            onClick={() => setDisclosureOpen(true)}
          >
            <FileText />
            {isRTL
              ? 'إظهار إفصاح الخصوصية للمريض'
              : 'Show the patient disclosure'}
          </Button>

          <Checkbox
            className="text-xs"
            checked={remember}
            onCheckedChange={setRemember}
            label={
              <span>
                {isRTL
                  ? 'لا تطلب مني التأكيد لباقي هذه الجلسة'
                  : "Don't ask me again for the rest of this work session"}
              </span>
            }
          />
        </div>

        <footer
          className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 sm:px-6 pb-5 pt-3 border-t"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <Button
            variant="secondary"
            onClick={handleCancel}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={submitting}
          >
            {!submitting && <Mic />}
            {isRTL ? 'موافقة وابدأ التسجيل' : 'I confirm — start recording'}
          </Button>
        </footer>
      </div>

      <PatientDisclosureModal
        open={disclosureOpen}
        onClose={() => setDisclosureOpen(false)}
      />
    </div>
  );
};
